<template>
  <div class="travel-calculator">
    <h2>{{ title }}</h2>

    <section v-if="hasRates">
      <form id="buynow">
        <!-- Select a currency output type (cash or card) -->

        <label for="currency" class="travel-calculator__label">{{ questionCurrencyOutput }}</label>
        <div class="travel-calculator__section travel-calculator__currency-output">
          <label v-if="cashOutput !== ''" class="travel-calculator__radio">
            <input type="radio" name="moneyType" value="cash" v-model="moneyType" />
            <span class="travel-calculator__radio-checkmark"></span>
            {{ cashOutput }}
          </label>

          <label v-if="cardOutput !== ''" class="travel-calculator__radio">
            <input type="radio" name="moneyType" value="card" v-model="moneyType" />
            <span class="travel-calculator__radio-checkmark"></span>
            {{ cardOutput }}
          </label>

          <label v-if="offerOutput !== '' && offerEnabled" class="travel-calculator__radio">
            <input type="radio" name="moneyType" value="offer" v-model="moneyType" />
            <span class="travel-calculator__radio-checkmark"></span>
            <span v-if="isMobileScreen">Cash & Card</span>
            <span v-else>{{ offerOutput }}</span>
            <span class="offer-text">{{ offerHighlightText }}</span>
          </label>
          <a :href="offerOutputLink" class="offer-link" :target="offerOutputTarget">{{ offerOutputButton }}</a>

          <p class="travel-calculator__radio-description">
            {{ descriptionCurrencyOutput }}
          </p>
        </div>

        <!-- Select a currency  -->

        <label for="targetCurrencyCodes" class="travel-calculator__label">{{ questionCurrency }}</label>

        <!-- Select a currency for the cash output  -->
        <div class="travel-calculator__section">
          <select class="calculator-select" id="targetCurrencyCodes" v-model="currencyCode" v-if="moneyType == 'cash'">
            <option v-for="exchangeRate in cashRates" :value="exchangeRate.currencyCode" :key="exchangeRate[0]">
              {{ exchangeRate.description }} ({{ exchangeRate.currencyCode }})
            </option>
          </select>

          <!-- Select a currency for the card output  -->

          <select class="calculator-select" id="targetCurrencyCodes" v-model="currencyCode" v-if="moneyType == 'card'">
            <option v-for="preExchangeRate in cardRates" :value="preExchangeRate.currencyCode"
              :key="preExchangeRate[0]">
              {{ preExchangeRate.description }} ({{ preExchangeRate.currencyCode }})
            </option>
          </select>

          <!-- Select a currency for the offer output  -->
          <select class="calculator-select" id="targetCurrencyCodes" v-model="currencyCode" v-if="moneyType == 'offer'">
            <option v-for="preExchangeRate in cardRates" :value="preExchangeRate.currencyCode"
              :key="preExchangeRate[0]">
              {{ preExchangeRate.description }} ({{ preExchangeRate.currencyCode }})
            </option>
          </select>
        </div>

        <!-- Input for the amount required -->

        <label for="amount" class="travel-calculator__label" value="question_amount">{{ questionAmount }}</label>

        <div class="travel-calculator__section travel-calculator__wrapper">
          <div class="travel-calculator-input-container travel-calculator-input-container--amount">
            <span class="travel-calculator__currency-code">£</span>
            <input name="question_amount" type="number" id="amount" v-model.number="currencyAmount"
              :min="moneyType === 'offer' ? 400 : null" class="travel-calculator__input" pattern="[0-9]*" />
          </div>
          <span class="travel-calculator__icon">=</span>

          <!-- Outputs the conversion amount -->

          <div class="travel-calculator__conversion">
            <span class="travel-calculator__currency-code">{{ currencyCode }}</span>
            <span class="travel-calculator__currency-amount">{{ currency(conversionAmount) }}</span>
          </div>
        </div>

        <!-- Promotion container or error message, shown when the offer radio is selected -->
        <div v-if="moneyType === 'offer'">
          <div class="error-message" v-if="currencyAmount < 400">
            <small>Offer is only applicable on purchases of £400 or more.</small>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20">
              <use xlink:href="/Static/images/sprites/symbols.svg#icon-info" />
            </svg>
          </div>
          <div>
            <h5>Example split between cash and card</h5>
            <p>Exact split of funds can be adjusted in your basket</p>
          </div>
          <div class="offer-container" v-if="currencyAmount >= 400">
            <div class="cash-section">
              <div class="offer-segment">
                <img src="/Static/images/svg/travel money.svg" class="icon" alt="Cash Icon">
                <span class="label">Cash</span>
              </div>
              <div class="amount">{{ currencyCode }} {{ currency(halfConversionAmount) }}</div>
            </div>
            <div class="plus">+</div>
            <div class="card-section">
              <div class="offer-segment">
                <img src="/Static/images/svg/credit cards.svg" class="icon" alt="Card Icon">
                <span class="label">Card</span>
              </div>

              <div class="amount">{{ currencyCode }} {{ currency(halfConversionAmount) }}</div>
            </div>
            <div class="offer-section" v-html="computedOfferText"></div>
          </div>
        </div>

        <!-- Outputs the exchange rate -->
        <label for="rate" class="travel-calculator__label">Are you an Asda Rewards member?</label>

        <div class="travel-calculator__section travel-calculator__results">
          <picture class="travel-calculator__asda-rewards-logo">
            <source srcset="/Static/images/asda-rewards.png" />
            <img src="/Static/images/asda-rewards.png" alt="Asda Money Rewards"
              class="travel-calculator__asda-rewards" />
          </picture>
          <div class="travel-calculator__rate-type-list">
            <label class="travel-calculator__radio">
              <input type="radio" name="rateType" value="promo" v-model="rateType" />
              <span class="travel-calculator__radio-checkmark"></span>
              <div class="travel-calculator__rate-type">
                <strong>Yes - rewards rate:</strong>
                <span>1 GBP = {{ promotionalRate }} {{ currencyCode }}</span>
              </div>
            </label>
            <label class="travel-calculator__radio">
              <input type="radio" name="rateType" value="general" v-model="rateType" />
              <span class="travel-calculator__radio-checkmark"></span>
              <div class="travel-calculator__rate-type">
                <strong>No - standard rate: </strong>
                <span>1 GBP = {{ currencyRate }} {{ currencyCode }}</span>
              </div>
            </label>
          </div>
        </div>

        <!-- Purchase currency button -->

        <div v-if="isButtonPopulated" class="travel-calculator-btn">
          <a @click.prevent="submitCalculator" :href="finalPurchaseLink" class="btn btn--blue"
            :target="purchaseTarget">{{ purchaseButton }}</a>
        </div>

        <!-- Asda rewards sign up -->

        <div v-if="isRewardsLink" class="travel-calculator-btn">
          <a :href="rewardsUrl" class="" :target="rewardsTarget">{{ rewardsName }}</a>
        </div>
      </form>
    </section>
    <slot v-else>
      <div v-if="isButtonPopulated" class="travel-calculator-btn">
        <a @click.prevent="submitCalculator" :href="finalPurchaseLink" class="btn btn--blue" :target="purchaseTarget">{{
          purchaseButton
          }}</a>
      </div>
    </slot>
  </div>
</template>

<script>
import lazyloadPicturefillBackground from 'lazyload-picturefill-background';

export default {
  name: 'travelCalculator',
  props: {
    title: {
      type: String,
      default: 'How much do you need?',
    },
    questionCurrencyOutput: {
      type: String,
      default: '1. How would you like your currency',
    },
    cashOutput: {
      type: String,
      default: 'Cash',
    },
    cardOutput: {
      type: String,
      default: 'Card',
    },
    offerOutput: {
      type: String,
      default: 'Test',
    },
    cardPrefix: {
      type: String,
      default: 'MCP',
    },
    descriptionCurrencyOutput: {
      type: String,
      default: 'Only selected currencies are available with the Asda Money Currency Card.',
    },
    questionCurrency: {
      type: String,
      default: '2. Type or select your destination currency',
    },
    questionAmount: {
      type: String,
      default: '3. How much would you like to change?',
    },
    exchangeRateText: {
      type: String,
    },
    cashRates: {
      type: Array,
      default: () => [],
    },
    cardRates: {
      type: Array,
      default: () => [],
    },
    purchaseLink: {
      type: String,
      default: 'https://travelmoney.asda.com/asda/purchase',
    },
    purchaseButton: {
      type: String,
      default: 'Buy currency',
    },
    purchaseTarget: {
      type: String,
      default: '',
    },
    defaultCurrency: {
      type: String,
      default: 'EUR',
    },
    rewardsUrl: {
      type: String,
      default: '',
    },
    rewardsName: {
      type: String,
      default: '',
    },
    rewardsTarget: {
      type: String,
      default: '',
    },
    offerEnabled: {
      type: Boolean,
      default: false,
    },
    offerHighlightText: {
      type: String,
      default: '',
    },
    offerOutputLink: {
      type: String,
      default: '',
    },
    offerOutputButton: {
      type: String,
      default: '',
    },
    offerOutputTarget: {
      type: String,
      default: '',
    },
    offerCurrencies: {
      type: String,
      default: '',
    },
    offerText: {
      type: String,
      default: '',
    },
  },

  data: function () {
    return {
      currencyAmount: 500,
      currencyCode: this.defaultCurrency,
      moneyType: 'cash',
      rateType: 'promo',
      isMobileScreen: false,
    };
  },

  computed: {
    hasRates() {
      return this.cashRates.length && this.cardRates.length;
    },

    currencyRate() {
      const rate = this.cashRates.find((rate) => rate.currencyCode === this.currencyCode);
      return rate ? rate.rate : 0;
    },

    promotionalRate() {
      const rate = this.cashRates.find((rate) => rate.currencyCode === this.currencyCode);
      return rate ? rate.promotionRate : 0;
    },

    conversionAmount() {
      if (this.rateType === 'promo') {
        return this.currencyAmount * this.promotionalRate;
      } else {
        return this.currencyAmount * this.currencyRate;
      }
    },

    halfConversionAmount() {
      return this.conversionAmount / 2;
    },

    isButtonPopulated() {
      return this.purchaseLink != '' && this.purchaseButton != '';
    },

    isRewardsLink() {
      return this.rewardsUrl != '' && this.rewardsName != '';
    },

    finalPurchaseLink() {
      const purchaseLinkParts = this.purchaseLink.split('?');
      const url = new URLSearchParams(purchaseLinkParts[1]);

      switch(this.moneyType) {
        case "card":
          url.append('productCode1', this.cardPrefix + this.currencyCode);
          url.append('amount1', this.currencyAmount);
          url.append('redirectToReload', 'true');
          break;

        case "offer": {
          let halvedAmount = this.currencyAmount / 2;
          url.append('productCode1', this.currencyCode);
          url.append('amount1', halvedAmount);
          url.append('productCode2', this.cardPrefix + this.currencyCode);
          url.append('amount2', halvedAmount);
          break;
        }

        default:
          url.append('productCode1', this.currencyCode);
          url.append('amount1', this.currencyAmount);
          break;
      }

      return purchaseLinkParts[0] + '?' + url.toString();
    },

    computedOfferText() {
      let offerValue = "";
      if (this.currencyAmount >= 1200) {
        offerValue = "£15";
      } else if (this.currencyAmount >= 800) {
        offerValue = "£10";
      } else if (this.currencyAmount >= 400) {
        offerValue = "£5";
      }
      return this.offerText.replace('{{offer_value}}', offerValue);
    },
  },

  watch: {
    moneyType(val) {
      const validMoneyTypes = ['cash', 'card', 'offer'];
      if (!validMoneyTypes.includes(val)) {
        this.currencyCode = this.defaultCurrency;
        return;
      }
      const rates = this[`${val}Rates`];
      if (!rates || !rates.find((rate) => rate.currencyCode === this.currencyCode)) {
        this.currencyCode = this.defaultCurrency;
      }
    },
  },

  methods: {
    currency(amount) {
      try {
        return new Intl.NumberFormat('en-GB', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(amount);
      } catch (err) {
        return amount;
      }
    },

    submitCalculator(e) {
      e.preventDefault();
      window.open(this.finalPurchaseLink);
      this.gtm();
    },

    gtm() {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'travelMoneyCalculator',
        currencyPreference: this.moneyType,
        destinationCurrency: this.currencyCode,
        changeAmount: this.currencyAmount,
        outputValue: this.conversionAmount,
      });
    },

    checkMobileScreen() {
      this.isMobileScreen = window.innerWidth <= 768;
    },
  },

  mounted() {
    new lazyloadPicturefillBackground();
    this.checkMobileScreen();
    window.addEventListener('resize', this.checkMobileScreen);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobileScreen);
  },
};
</script>
